/**
 * Databases on top of pages (see `@nexus/core` database.ts): the schema lives on
 * the database page, rows are its child pages, values are their properties.
 */
import {
  computeRows,
  DATABASE_PROP,
  isComputed,
  isEmptyValue,
  rawValue,
  readSchema,
  RECURRENCE_PRESETS,
  shortId,
  relatedDatabaseIds,
  TEMPLATE_PROP,
  templateSchema,
  valueKey,
  type DatabaseSchema,
  type DatabaseTemplate,
  type PropertyDef,
} from '@nexus/core';
import type { PageRecord, Store } from '@nexus/store';
import type { JSONContent } from '@tiptap/core';
import { ensureTaskIds } from '@nexus/editor';
import { isEmptyDoc, loadPageJSON } from './content';
import { mutatePageDoc } from './docs';
import { tasksFolder } from './tasks';

export interface DatabaseRow {
  id: string;
  title: string;
  icon: string | null;
  createdAt: number;
  updatedAt: number;
  props: Record<string, unknown>;
  /** Backlink, rollup and formula values by property id. */
  computed?: Record<string, unknown>;
}

export interface LoadedDatabase {
  schema: DatabaseSchema;
  rows: DatabaseRow[];
  /** Template rows (`nexus.template`), not shown in views. */
  templates: DatabaseRow[];
  /** Databases this one links to (relations, backlinks), with their rows. */
  related: Map<string, { title: string; schema: DatabaseSchema; rows: DatabaseRow[] }>;
}

const toRow = (p: PageRecord & { props: Record<string, unknown> }): DatabaseRow => ({
  id: p.id,
  title: p.title,
  icon: p.icon,
  createdAt: p.createdAt,
  updatedAt: p.updatedAt,
  props: p.props,
});

/** Rows and templates of a database page. */
async function rowsOf(store: Store, pageId: string) {
  const all = (await store.listPagesWithProps({ parentId: pageId })).map(toRow);
  return {
    rows: all.filter((r) => r.props[TEMPLATE_PROP] !== true),
    templates: all.filter((r) => r.props[TEMPLATE_PROP] === true),
  };
}

const localToday = () => {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

export async function getSchema(store: Store, pageId: string): Promise<DatabaseSchema | null> {
  return readSchema(await store.getProps(pageId));
}

/**
 * A database's schema, rows (with computed values) and templates, plus the
 * databases it links to; null when the page is not a database.
 */
export async function loadDatabase(
  store: Store,
  pageId: string,
  today = localToday(),
): Promise<LoadedDatabase | null> {
  const schema = await getSchema(store, pageId);
  if (!schema) return null;
  const { rows, templates } = await rowsOf(store, pageId);
  const related: LoadedDatabase['related'] = new Map();
  for (const id of relatedDatabaseIds(schema)) {
    const other = id === pageId ? schema : await getSchema(store, id);
    const page = await store.getPage(id);
    if (!other || !page || page.deleted || page.trashedAt) continue;
    related.set(id, {
      title: page.title,
      schema: other,
      rows: id === pageId ? rows : (await rowsOf(store, id)).rows,
    });
  }
  return { schema, rows: computeRows(schema, rows, related, today), templates, related };
}

export const saveSchema = (store: Store, pageId: string, schema: DatabaseSchema) =>
  store.setProp(pageId, DATABASE_PROP, schema);

/** Give each row the template's default properties when it lacks them. */
async function applyRowDefaults(store: Store, pageId: string, schema: DatabaseSchema) {
  const defaults = Object.entries(schema.rowDefaults ?? {});
  if (!defaults.length) return;
  for (const row of await store.listPagesWithProps({ parentId: pageId })) {
    for (const [key, value] of defaults) {
      const has = Object.keys(row.props).some((k) => k.toLowerCase() === key.toLowerCase());
      if (!has) await store.setProp(row.id, key, value);
    }
  }
}

/** Turn an existing page into a database; its subpages become the rows. */
export async function makeDatabase(
  store: Store,
  pageId: string,
  template: DatabaseTemplate = 'empty',
): Promise<DatabaseSchema> {
  const existing = await getSchema(store, pageId);
  if (existing) return existing;
  const schema = templateSchema(template);
  await saveSchema(store, pageId, schema);
  await applyRowDefaults(store, pageId, schema);
  return schema;
}

/**
 * Turn a database back into an ordinary page: only the schema (columns,
 * views) goes; the page's text, its subpages and their properties stay.
 */
export async function unmakeDatabase(store: Store, pageId: string): Promise<void> {
  if (await getSchema(store, pageId)) await store.setProp(pageId, DATABASE_PROP, undefined);
}

export async function createDatabase(
  store: Store,
  input: {
    title: string;
    icon?: string | null;
    parentId?: string | null;
    template?: DatabaseTemplate;
  },
): Promise<PageRecord> {
  const page = await store.createPage({
    title: input.title,
    icon: input.icon ?? null,
    parentId: input.parentId ?? null,
  });
  await makeDatabase(store, page.id, input.template ?? 'empty');
  return page;
}

/** Store a value on a row (empty values remove the property). */
export async function setRowValue(
  store: Store,
  rowId: string,
  def: Pick<PropertyDef, 'name' | 'keys'>,
  value: unknown,
): Promise<void> {
  const key = valueKey(await store.getProps(rowId), def);
  await store.setProp(rowId, key, isEmptyValue(value) ? undefined : value);
}

/** Task ids must be unique: a copied checkbox gets a fresh one. */
export function withoutTaskIds(node: JSONContent): JSONContent {
  const attrs = node.attrs && 'taskId' in node.attrs ? { ...node.attrs, taskId: null } : node.attrs;
  return {
    ...node,
    ...(attrs ? { attrs } : {}),
    ...(node.content ? { content: node.content.map(withoutTaskIds) } : {}),
  };
}

/**
 * A new row: defaults (e.g. `type: project`), the first option of each status
 * property (like Notion), then the given values (by property id). With a
 * template the row starts as a copy of it: title, properties and content.
 */
export async function addRow(
  store: Store,
  databaseId: string,
  schema: DatabaseSchema,
  input: { title?: string; values?: Record<string, unknown>; templateId?: string } = {},
): Promise<PageRecord> {
  const template = input.templateId ? await store.getPage(input.templateId) : null;
  const page = await store.createPage({
    title: input.title ?? template?.title ?? '',
    icon: template?.icon ?? null,
    parentId: databaseId,
  });
  for (const [key, value] of Object.entries(schema.rowDefaults ?? {}))
    await store.setProp(page.id, key, value);
  const fromTemplate = template ? await store.getProps(template.id) : {};
  for (const [key, value] of Object.entries(fromTemplate))
    if (!key.startsWith(TEMPLATE_PROP)) await store.setProp(page.id, key, value);
  for (const def of schema.properties) {
    if (isComputed(def)) continue;
    const given = input.values?.[def.id];
    const current = rawValue(fromTemplate, def);
    const value =
      given !== undefined
        ? given
        : !isEmptyValue(current)
          ? undefined // already copied from the template
          : def.type === 'status'
            ? def.options?.[0]?.name
            : undefined;
    if (!isEmptyValue(value)) await setRowValue(store, page.id, def, value);
  }
  if (template) {
    const doc = await loadPageJSON(store, template.id);
    if (!isEmptyDoc(doc)) {
      const { doc: copy } = ensureTaskIds(withoutTaskIds(doc));
      await mutatePageDoc(store, page.id, () => copy);
    }
  }
  return page;
}

/** A new template for the database: a hidden row you edit like any page. */
export async function createTemplate(
  store: Store,
  databaseId: string,
  schema: DatabaseSchema,
  title: string,
): Promise<PageRecord> {
  const page = await store.createPage({ title, parentId: databaseId });
  await store.setProp(page.id, TEMPLATE_PROP, true);
  for (const [key, value] of Object.entries(schema.rowDefaults ?? {}))
    await store.setProp(page.id, key, value);
  return page;
}

/** The database a page is a row of (its parent, if that is a database). */
export async function parentDatabase(
  store: Store,
  page: Pick<PageRecord, 'parentId'>,
): Promise<{ id: string; schema: DatabaseSchema } | null> {
  if (!page.parentId) return null;
  const schema = await getSchema(store, page.parentId);
  return schema ? { id: page.parentId, schema } : null;
}

/** A page is free to become a database: no content of its own and no subpages. */
async function isBlank(store: Store, page: PageRecord): Promise<boolean> {
  const children = await store.listPagesWithProps({ parentId: page.id });
  const props = await store.getProps(page.id);
  return (
    children.length === 0 &&
    Object.keys(props).length === 0 &&
    isEmptyDoc(await loadPageJSON(store, page.id))
  );
}

/**
 * The starter databases: the tasks folder becomes the Taken database, and
 * Projecten and Vergaderingen are created (or an empty page with that name is
 * used). Remembered in synced settings, so other devices find the same pages.
 */
export async function ensureStarterDatabases(store: Store): Promise<void> {
  const tasks = await tasksFolder(store);
  await makeDatabase(store, tasks.id, 'tasks');

  const starters: [string, string, string, DatabaseTemplate][] = [
    ['projects_db_id', 'Projecten', '🚀', 'projects'],
    ['meetings_db_id', 'Vergaderingen', '🗓️', 'meetings'],
  ];
  for (const [key, title, icon, template] of starters) {
    const id = await store.getSetting(key);
    const known = id ? await store.getPage(id) : null;
    if (known && !known.deleted && !known.trashedAt) {
      await makeDatabase(store, known.id, template);
      continue;
    }
    // Reuse a blank page with that title (e.g. made by hand); never convert a page
    // that already has content or subpages — that is the user's own structure.
    const sameTitle = (await store.listPages()).filter(
      (p) => p.title.toLowerCase() === title.toLowerCase(),
    );
    let page: PageRecord | undefined;
    for (const p of sameTitle) {
      if ((await getSchema(store, p.id)) || (await isBlank(store, p))) {
        page = p;
        break;
      }
    }
    page ??= await store.createPage({ title, icon });
    await makeDatabase(store, page.id, template);
    await store.setSetting(key, page.id);
  }
  await upgradeStarterDatabases(store);
  await upgradeStarterDatabasesV3(store);
}

const hasProp = (schema: DatabaseSchema, name: string) =>
  schema.properties.some(
    (p) =>
      p.name.toLowerCase() === name.toLowerCase() ||
      p.keys?.some((k) => k.toLowerCase() === name.toLowerCase()),
  );
const newProp = (def: Omit<PropertyDef, 'id'>): PropertyDef => ({ id: shortId(), ...def });

/**
 * Fase 5b additions to the starter databases (safe to run again):
 * - Taken: Project links to Projecten (relation), Herhaling, Dagen resterend,
 *   and a "Volgende keer" button for recurring tasks;
 * - Projecten: its Taken (backlink) and Voortgang (% done);
 * - Vergaderingen: Project links to Projecten.
 */
export async function upgradeStarterDatabases(store: Store): Promise<void> {
  const tasksId = await store.getSetting('tasks_parent_id');
  const projectsId = await store.getSetting('projects_db_id');
  const meetingsId = await store.getSetting('meetings_db_id');
  const tasks = tasksId ? await getSchema(store, tasksId) : null;
  const projects = projectsId ? await getSchema(store, projectsId) : null;

  // A plain page link to a project becomes a relation to the Projecten database.
  const linkToProjects = (schema: DatabaseSchema): DatabaseSchema =>
    !projectsId
      ? schema
      : {
          ...schema,
          properties: schema.properties.map((p) =>
            p.type === 'page' && p.keys?.includes('project')
              ? { ...p, type: 'relation', relation: { databaseId: projectsId } }
              : p,
          ),
        };

  if (tasksId && tasks) {
    let next = linkToProjects(tasks);
    // An early 5a schema had the reminder as plain text.
    next = {
      ...next,
      properties: next.properties.map((p) =>
        p.type === 'text' && p.keys?.includes('reminder') ? { ...p, type: 'reminder' } : p,
      ),
    };
    const add = (def: Omit<PropertyDef, 'id'>) => {
      if (!hasProp(next, def.name))
        next = { ...next, properties: [...next.properties, newProp(def)] };
    };
    add({
      name: 'Herhaling',
      keys: ['recurrence', 'repeat', 'herhaling'],
      type: 'text',
      group: 'Details',
    });
    add({
      name: 'Dagen resterend',
      type: 'formula',
      group: 'Overzicht',
      formula: { expression: 'if(empty(prop("Deadline")), "", days_until(prop("Deadline")))' },
    });
    add({ name: 'Volgende keer', type: 'button', group: 'Details', button: { action: 'recur' } });
    await saveSchema(store, tasksId, next);
  }

  if (projectsId && projects && tasksId) {
    const tasksNow = await getSchema(store, tasksId);
    const relation = tasksNow?.properties.find(
      (p) => p.type === 'relation' && p.keys?.includes('project'),
    );
    const status = tasksNow?.properties.find((p) => p.type === 'status');
    let next = projects;
    if (relation && !hasProp(next, 'Taken')) {
      const backlink = newProp({
        name: 'Taken',
        type: 'backlink',
        backlink: { databaseId: tasksId, propertyId: relation.id },
      });
      next = { ...next, properties: [...next.properties, backlink] };
      if (status && !hasProp(next, 'Voortgang'))
        next = {
          ...next,
          properties: [
            ...next.properties,
            newProp({
              name: 'Voortgang',
              type: 'rollup',
              rollup: {
                via: backlink.id,
                target: status.id,
                fn: 'percent_matching',
                match: { op: 'is', value: 'Done' },
              },
            }),
          ],
        };
    }
    await saveSchema(store, projectsId, next);
  }

  if (meetingsId) {
    const meetings = await getSchema(store, meetingsId);
    if (meetings) await saveSchema(store, meetingsId, linkToProjects(meetings));
  }
}

const STARTERS: { key: string; title: string; template: DatabaseTemplate }[] = [
  { key: 'tasks_parent_id', title: 'Taken', template: 'tasks' },
  { key: 'projects_db_id', title: 'Projecten', template: 'projects' },
  { key: 'meetings_db_id', title: 'Vergaderingen', template: 'meetings' },
];

/** A database made by `ensureStarterDatabases`: the template's properties. */
const isStarterCopy = (schema: DatabaseSchema, template: DatabaseTemplate) =>
  templateSchema(template).properties.every((p) => hasProp(schema, p.name));

export interface StarterDedupeResult {
  /** Rows (and templates) moved into the kept database. */
  moved: number;
  /** Duplicate databases put in the trash. */
  trashed: string[];
}

/**
 * Two devices that each made the starter databases before they first synced
 * (e.g. one used signed out) end up with every starter database twice. Keep the
 * oldest copy of each — every device picks the same one, whatever settings have
 * arrived yet — move the rows and templates of the others into it, and trash the
 * emptied copies (never a copy with content of its own). The synced settings are
 * pointed at the kept copies, so the tasks folder stays the kept Taken database.
 */
export async function dedupeStarterDatabases(store: Store): Promise<StarterDedupeResult> {
  const result: StarterDedupeResult = { moved: 0, trashed: [] };
  const pages = [...(await store.listPages()), ...(await store.listPages({ trashed: true }))];
  const schemas = new Map<string, DatabaseSchema>();
  const replaced = new Map<string, string>(); // duplicate → kept
  const kept: string[] = [];

  for (const s of STARTERS) {
    const copies: PageRecord[] = [];
    for (const page of pages) {
      if (page.title.toLowerCase() !== s.title.toLowerCase()) continue;
      const schema = await getSchema(store, page.id);
      if (!schema || !isStarterCopy(schema, s.template)) continue;
      schemas.set(page.id, schema);
      copies.push(page);
    }
    const keep = copies
      .filter((p) => !p.trashedAt)
      .sort((a, b) => a.createdAt - b.createdAt || a.id.localeCompare(b.id))[0];
    if (!keep) continue;
    kept.push(keep.id);
    if ((await store.getSetting(s.key)) !== keep.id) await store.setSetting(s.key, keep.id);

    for (const dup of copies) {
      if (dup.id === keep.id) continue;
      replaced.set(dup.id, keep.id);
      // A trashed copy only matters when rows arrived after it was trashed.
      const children = await store.listPagesWithProps({ parentId: dup.id });
      let after = (await store.listPagesWithProps({ parentId: keep.id })).at(-1)?.id ?? null;
      for (const child of children) {
        await store.movePage(child.id, keep.id, after);
        after = child.id;
        result.moved++;
      }
      // Properties the user added to the duplicate (computed ones point into it).
      const extra = schemas
        .get(dup.id)!
        .properties.filter((p) => p.type !== 'backlink' && p.type !== 'rollup');
      const target = (await getSchema(store, keep.id))!;
      const missing = extra.filter((p) => !hasProp(target, p.name));
      if (missing.length)
        await saveSchema(store, keep.id, {
          ...target,
          properties: [...target.properties, ...missing],
        });
      if (!dup.trashedAt && isEmptyDoc(await loadPageJSON(store, dup.id))) {
        await store.trashPage(dup.id);
        result.trashed.push(dup.id);
      }
    }
  }
  if (replaced.size) await relinkStarters(store, kept, replaced, schemas);
  return result;
}

/**
 * The kept databases may come from different devices (one copy of Taken, another
 * of Projecten): point relations, backlinks and rollups at the kept copies,
 * matching properties of a replaced database by name.
 */
async function relinkStarters(
  store: Store,
  kept: string[],
  replaced: Map<string, string>,
  before: Map<string, DatabaseSchema>,
) {
  const sameProp = async (oldDb: string, propId: string, newDb: string) => {
    const name = before.get(oldDb)?.properties.find((p) => p.id === propId)?.name;
    const target = await getSchema(store, newDb);
    return name
      ? target?.properties.find((p) => p.name.toLowerCase() === name.toLowerCase())?.id
      : undefined;
  };
  for (const id of kept) {
    const schema = await getSchema(store, id);
    if (!schema) continue;
    let changed = false;
    const properties: PropertyDef[] = [];
    for (const p of schema.properties) {
      let next = p;
      const rel = p.relation?.databaseId;
      if (rel && replaced.has(rel)) next = { ...p, relation: { databaseId: replaced.get(rel)! } };
      const back = p.backlink;
      if (back && replaced.has(back.databaseId)) {
        const db = replaced.get(back.databaseId)!;
        const propertyId =
          (await sameProp(back.databaseId, back.propertyId, db)) ?? back.propertyId;
        next = { ...p, backlink: { databaseId: db, propertyId } };
      }
      const roll = p.rollup;
      if (roll) {
        const via = schema.properties.find((x) => x.id === roll.via);
        const from = via?.relation?.databaseId ?? via?.backlink?.databaseId;
        if (from && replaced.has(from)) {
          const target = await sameProp(from, roll.target, replaced.get(from)!);
          if (target) next = { ...p, rollup: { ...roll, target } };
        }
      }
      changed ||= next !== p;
      properties.push(next);
    }
    if (changed) await saveSchema(store, id, { ...schema, properties });
  }
}

/** Distinct non-empty values of one property over a database's rows (for dropdown options). */
async function usedValues(store: Store, databaseId: string, keys: string[]): Promise<string[]> {
  const seen = new Map<string, string>();
  for (const row of (await store.listPages()).filter(
    (p) => p.parentId === databaseId && !p.deleted && !p.trashedAt,
  )) {
    const props = await store.getProps(row.id);
    for (const [k, v] of Object.entries(props)) {
      if (!keys.includes(k.toLowerCase())) continue;
      for (const item of Array.isArray(v) ? v : [v]) {
        const text = typeof item === 'string' ? item.trim() : '';
        if (text && !seen.has(text.toLowerCase())) seen.set(text.toLowerCase(), text);
      }
    }
  }
  return [...seen.values()];
}

/**
 * Clearer properties in the starter databases (safe to run again):
 * - Taken: Herhaling becomes a dropdown with the usual rules (plus the ones in use),
 *   and Gepland / Startdatum dates are added (the task logic already reads them);
 * - Eigenaar (Taken, Projecten) gets the names in use as dropdown options.
 */
export async function upgradeStarterDatabasesV3(store: Store): Promise<void> {
  const tasksId = await store.getSetting('tasks_parent_id');
  const projectsId = await store.getSetting('projects_db_id');
  const withOwners = async (id: string, schema: DatabaseSchema): Promise<DatabaseSchema> => {
    const owner = schema.properties.find((p) => p.type === 'person');
    if (!owner) return schema;
    const names = await usedValues(store, id, [owner.name.toLowerCase(), ...(owner.keys ?? [])]);
    const known = new Set((owner.options ?? []).map((o) => o.name.toLowerCase()));
    const added = names.filter((n) => !known.has(n.toLowerCase()));
    if (!added.length && owner.options) return schema;
    const options = [
      ...(owner.options ?? []),
      ...added.map((name) => ({ name, color: 'gray' as const })),
    ];
    return {
      ...schema,
      properties: schema.properties.map((p) => (p.id === owner.id ? { ...p, options } : p)),
    };
  };

  const tasks = tasksId ? await getSchema(store, tasksId) : null;
  if (tasksId && tasks) {
    let next = tasks;
    const recurrence = next.properties.find((p) => p.keys?.includes('recurrence'));
    if (recurrence && recurrence.type === 'text') {
      const inUse = await usedValues(store, tasksId, [
        recurrence.name.toLowerCase(),
        ...(recurrence.keys ?? []),
      ]);
      const names = [...RECURRENCE_PRESETS];
      for (const v of inUse)
        if (!names.some((n) => n.toLowerCase() === v.toLowerCase())) names.push(v);
      next = {
        ...next,
        properties: next.properties.map((p) =>
          p.id === recurrence.id
            ? {
                ...p,
                type: 'select',
                options: names.map((name) => ({ name, color: 'purple' as const })),
              }
            : p,
        ),
      };
    }
    const add = (def: Omit<PropertyDef, 'id'>) => {
      if (!hasProp(next, def.name) && !def.keys?.some((k) => hasProp(next, k)))
        next = { ...next, properties: [...next.properties, newProp(def)] };
    };
    add({ name: 'Gepland', keys: ['scheduled', 'gepland'], type: 'date', group: 'Details' });
    add({ name: 'Startdatum', keys: ['start', 'startdatum'], type: 'date', group: 'Details' });
    next = await withOwners(tasksId, next);
    await saveSchema(store, tasksId, next);
  }
  const projects = projectsId ? await getSchema(store, projectsId) : null;
  if (projectsId && projects)
    await saveSchema(store, projectsId, await withOwners(projectsId, projects));
}
